import React from 'react';
import { LinkButton } from '@sato/components';
import { observer } from 'mobx-react-lite';

import { ILinkButtonFields, IWidget, ParsedEntry } from '@/contentful/types';

import { Link as SatoLink } from '@components/common/Link';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { useParkingSpaceContracts } from '@hooks/useParkingSpaceContracts';
import { ROUTES } from '@routes/routes.constants';
import { canSwitchParkingSpace } from '@views/parking/parking.utils';

import { linkButtonVariantMap } from '../../utils/entry.utils';

import { WidgetButtonLayout } from './widgetButton.utils';

/**
 * Link to switching parking spaces. Shown only when the user has a parking contract they are allowed to switch
 * (same rule as the switch link on the parking front page), and links with that contract's ID.
 *
 * Contentful options:
 * - `customTranslations.description`: text shown above the button, hidden together with the button
 * - `customTranslations.buttonText`: button text, defaults to the app translation
 * - `parameters.variant`: `"contained" | "outlined" | "text"`, same values as in Link Button
 */
export const ParkingSpaceSwitchWidget = observer(
  ({ customTranslations, parameters }: ParsedEntry<IWidget>) => {
    const { t, tContentful } = useCustomTranslations(customTranslations);
    const variant = parameters?.variant as ILinkButtonFields['variant'];
    const { all, isLoading } = useParkingSpaceContracts();

    // If there are several, the switch page lets the user change the contract
    const switchableContract = all.find(canSwitchParkingSpace);

    if (isLoading || !switchableContract) return null;

    return (
      <WidgetButtonLayout description={t('description')}>
        <LinkButton<SatoLink<typeof ROUTES.SWITCH_PARKING_SPACES_LISTING>>
          linkComponent={SatoLink}
          to={ROUTES.SWITCH_PARKING_SPACES_LISTING}
          query={{ parkingContractId: switchableContract.parkingContractId }}
          variant={linkButtonVariantMap[variant ?? 'no variant']}
          iconPosition="right"
          icon="agreementCar"
          className="w-fit"
        >
          {t('buttonText') ||
            tContentful('parking.change_parking_space_link_title')}
        </LinkButton>
      </WidgetButtonLayout>
    );
  }
);
